// Location helper that works both in web preview and inside a Capacitor
// native shell (Android / iOS). It abstracts:
//
//   • Permission state discovery (granted / denied / prompt / restricted)
//   • Requesting one-shot high-accuracy position
//   • Opening the OS Settings screen so the user can flip a
//     permanently-denied permission back on
//
// The two-tier design (Capacitor first, browser fallback) keeps the same
// call-site for AddressDialog regardless of environment. Every method
// returns a structured `{ status, coords?, error? }` so callers can drive
// their own UI (native alerts, banners, dialogs) without spraying
// try/catch around geolocation logic.

const inCapacitor = () =>
  typeof window !== "undefined" && !!window.Capacitor?.isNativePlatform?.();

const capacitorPlatform = () =>
  (typeof window !== "undefined" && window.Capacitor?.getPlatform?.()) || "web";

/** Resolve the Capacitor Geolocation plugin lazily.
 *  Returns `null` on regular web (plugin not bundled).
 *
 *  IMPORTANT: never `return mod.Geolocation` directly. Capacitor plugins
 *  are Proxy objects that answer to ANY property access — including
 *  `.then` — with a callable native-bridge wrapper. If that bare proxy
 *  becomes the fulfillment value of this async function, JS's promise
 *  resolution algorithm sees a callable `.then` and treats it as a
 *  thenable, calling `proxy.then(...)` to "unwrap" it. That gets
 *  forwarded to native as a call to a plugin method literally named
 *  "then", which doesn't exist — throwing
 *  `"Geolocation.then() is not implemented on android"` before any real
 *  method (checkPermissions/getCurrentPosition) is ever invoked.
 *  Wrapping the proxy in a plain object sidesteps the thenable check. */
async function getCapGeolocation() {
  if (!inCapacitor()) return null;
  try {
    const mod = await import("@capacitor/geolocation");
    return { Geo: mod.Geolocation };
  } catch { return null; }
}

/** Resolve the Capacitor App plugin lazily. Used to open OS settings.
 *  See the thenable-proxy warning on getCapGeolocation() above — same
 *  reasoning applies here. */
async function getCapApp() {
  if (!inCapacitor()) return null;
  try {
    const mod = await import("@capacitor/app");
    return { App: mod.App };
  } catch { return null; }
}

/** Discover the current permission state without prompting.
 *
 * Returns one of:
 *   "granted"     — location is usable, no prompt needed
 *   "prompt"      — no decision yet, calling getCurrentPosition will show the OS prompt
 *   "denied"      — user said no. On native this could be dismissible (ask again works) or permanent.
 *   "restricted"  — iOS parental controls / MDM policy blocks location entirely (can't self-fix)
 *   "unsupported" — API not available in this environment
 */
export async function checkLocationPermission() {
  const cap = await getCapGeolocation().catch(() => null);
  if (cap) {
    try {
      const r = await cap.Geo.checkPermissions();
      // Capacitor exposes `location` for fine, `coarseLocation` on Android 12+.
      const state = r.location || r.coarseLocation || "prompt";
      return state;
    } catch { return "prompt"; }
  }
  // Web fallback via the standard Permissions API where available.
  if (typeof navigator !== "undefined" && navigator.permissions?.query) {
    try {
      const r = await navigator.permissions.query({ name: "geolocation" });
      return r.state; // "granted" | "denied" | "prompt"
    } catch { /* fall through */ }
  }
  if (typeof navigator === "undefined" || !navigator.geolocation) return "unsupported";
  return "prompt";
}

/** Request permission + one-shot position.
 *
 * Returns `{ status, coords?, error? }` where status matches
 * `checkLocationPermission` values plus:
 *   - "unavailable"  → hardware / network could not deliver a fix (timeout)
 *   - "granted"      → coords set: { lat, lng, accuracy }
 */
export async function requestLocation({ timeoutMs = 8000 } = {}) {
  const cap = await getCapGeolocation().catch(() => null);
  if (cap) {
    const { Geo } = cap;
    try {
      const before = await Geo.checkPermissions().catch(() => ({}));
      const beforeState = before.location || before.coarseLocation || "prompt";
      if (beforeState !== "granted") {
        const perm = await Geo.requestPermissions();
        const afterState = perm.location || perm.coarseLocation || "denied";
        if (afterState !== "granted") {
          return {
            status: afterState,
            permanent: beforeState === "denied" && afterState === "denied",
          };
        }
      }
      const pos = await Geo.getCurrentPosition({
        enableHighAccuracy: true,
        timeout: timeoutMs,
        maximumAge: 60000,
      });
      return {
        status: "granted",
        coords: {
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
        },
      };
    } catch (e) {
      // Capacitor throws a native "denied" error when a user has
      // previously permanently denied — surface it as "denied" so the
      // UI can suggest opening Settings.
      const msg = (e && e.message) || "";
      if (/denied|not allowed|prohibited/i.test(msg)) return { status: "denied", permanent: true, error: msg };
      if (/timeout|position/i.test(msg)) return { status: "unavailable", error: msg };
      return { status: "unavailable", error: msg };
    }
  }
  // Web fallback
  if (typeof navigator === "undefined" || !navigator.geolocation) {
    return { status: "unsupported" };
  }
  // Detect permanent browser denial via the Permissions API when available.
  let wasDenied = false;
  if (navigator.permissions?.query) {
    try {
      const r = await navigator.permissions.query({ name: "geolocation" });
      wasDenied = r.state === "denied";
    } catch { /* ignore */ }
  }
  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({
        status: "granted",
        coords: {
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
        },
      }),
      (err) => {
        if (err.code === err.PERMISSION_DENIED) return resolve({ status: "denied", permanent: wasDenied });
        if (err.code === err.POSITION_UNAVAILABLE) return resolve({ status: "unavailable" });
        if (err.code === err.TIMEOUT) return resolve({ status: "unavailable", error: "timeout" });
        resolve({ status: "unavailable", error: err.message });
      },
      { enableHighAccuracy: true, timeout: timeoutMs, maximumAge: 60000 },
    );
  });
}

/** Open the OS Settings screen for this app so the user can flip a
 *  permanently-denied permission back on. Returns `true` on success,
 *  `false` if we couldn't route them (in which case the caller can
 *  show step-by-step manual instructions instead).
 *
 *  Uses `@capacitor-community/native-settings` (bundled at native
 *  build time) which supports both iOS `app-settings:` and Android's
 *  `APPLICATION_DETAILS_SETTINGS` intent — the two officially-blessed
 *  routes on each platform. Falls back to `@capacitor/app openUrl`
 *  for iOS when the native-settings plugin isn't available.
 */
export async function openAppSettings() {
  if (!inCapacitor()) return false;
  // 1) Preferred: capacitor-native-settings (proper cross-platform routing).
  try {
    const mod = await import("capacitor-native-settings");
    if (mod?.NativeSettings) {
      const platform = capacitorPlatform();
      if (platform === "ios") {
        // openIOS accepts an IOSSettings enum value — App = the app's settings page.
        const IOSSettings = mod.IOSSettings;
        await mod.NativeSettings.openIOS({
          option: IOSSettings?.App || "App",
        });
      } else {
        const AndroidSettings = mod.AndroidSettings;
        await mod.NativeSettings.openAndroid({
          option: AndroidSettings?.ApplicationDetails || "application_details",
        });
      }
      return true;
    }
  } catch { /* plugin missing — fall through */ }

  // 2) Fallback: use App.openUrl. iOS accepts `app-settings:` natively.
  const cap = await getCapApp().catch(() => null);
  if (!cap) return false;
  const { App } = cap;
  const platform = capacitorPlatform();
  try {
    if (platform === "ios") {
      await App.openUrl({ url: "app-settings:" });
      return true;
    }
    if (platform === "android") {
      // No universal deep-link on Android without the NativeSettings
      // plugin — signal failure so the UI shows manual instructions.
      return false;
    }
  } catch { /* fall through */ }
  return false;
}

/** Convenient bool: is this a native shell where "Open Settings" makes
 *  sense? Used to hide the button on the web preview where it wouldn't
 *  actually go anywhere useful. */
export const canOpenSettings = () => inCapacitor();
